import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { normalizeIndianMobile } from "./login";
import { humanRefused, withHuman } from "../auth/human";

/**
 * The logins an operator hands out -- radaccess v1.119 (owner 2026-10-18,
 * radius-wiki operator-staff-and-portal-design): its STAFF, and the OWNERS of
 * its sub-operators. RBAC resource `operatorlogin`, held by Operator (and
 * superadmin) only -- no staff role may invite, re-role or disable a login.
 *
 * 🔑 Nobody sets another person's password. The operator names the person and
 * the roles; the person opens /invite, proves the mobile with an SMS code and
 * chooses a username and the password (radaccess v1.124: mobile first, then
 * the details, on a short-lived accept ticket).
 */
export type LoginKind = "staff" | "suboperator" | "owner";
export type LoginStatus = "PENDING" | "ACTIVE" | "DISABLED" | "CANCELLED" | "EXPIRED";
export type OperatorStaffRole = "manager" | "billing" | "support" | "technical" | "viewer";
/** io.ankhos's own staff (owner 2026-10-24): granted only at io.ankhos (radaccess v1.154). */
export type PlatformStaffRole = "platform_admin" | "platform_billing" | "platform_onboarding" | "platform_viewer";
export type StaffRole = OperatorStaffRole | PlatformStaffRole;

export const STAFF_ROLES: readonly { role: StaffRole; label: string; help: string }[] = [
  // ZITADEL role `manager` (never `admin`: the backends read admin as the platform superadmin).
  { role: "manager", label: "Admin", help: "Senior manager: everything the operator can do in this business -- plans, prices, subscribers, sub-operators, pins, billing. Cannot invite or remove staff." },
  { role: "billing", label: "Billing", help: "Recharge, receipts, PIN orders and top-ups. Cannot approve." },
  { role: "support", label: "Support", help: "Find a subscriber, User diagnose, disable or enable a subscriber." },
  { role: "technical", label: "Technical", help: "User diagnose, Change Device MAC, hotspot and reports." },
  { role: "viewer", label: "Viewer", help: "Read-only: reports and lists." },
];

/**
 * The roles io.ankhos gives its own staff. The gateway refuses these anywhere
 * but io.ankhos, and refuses the operator roles above at io.ankhos. Purge, staff
 * and payment gateway accounts stay the owner's.
 */
export const PLATFORM_STAFF_ROLES: readonly { role: StaffRole; label: string; help: string }[] = [
  { role: "platform_admin", label: "Platform admin", help: "Clients, invitations, promotions and Client accounts. Cannot purge, invite staff or set up payment gateways." },
  { role: "platform_billing", label: "Platform billing", help: "Client accounts: open accounts, raise invoices. Clients read-only." },
  { role: "platform_onboarding", label: "Platform onboarding", help: "Clients, invitations and promotions: invite, resend, suspend, reactivate. No billing." },
  { role: "platform_viewer", label: "Platform viewer", help: "Read-only: tenants, customers, invoices and the SMS log." },
];

/** The roles a staff login in namespace ns may hold. */
export function staffRolesFor(ns: string | null | undefined): readonly { role: StaffRole; label: string; help: string }[] {
  return ns === "io.ankhos" ? PLATFORM_STAFF_ROLES : STAFF_ROLES;
}

export interface OperatorLogin {
  id: number;
  namespace: string;
  invitedbynamespace: string;
  kind: LoginKind;
  firstname: string;
  lastname: string;
  mobile: string;
  roles: string[];
  status: LoginStatus;
  expiresat: string;
  acceptedat?: string;
  disabledat?: string;
  statusnote?: string;
  createdby: string;
  createddate: string;
}

/**
 * Whether radaccess texted the invitation (v1.150+, approved DLT staff_invite /
 * suboperator_invite): "sent", "failed", or "off" (SMS not switched on).
 * Absent from older radaccess.
 */
export type InviteSMS = "sent" | "failed" | "off";

/** A login as an invitation call returns it: the row plus the SMS outcome. */
export type InvitedLogin = OperatorLogin & { sms?: InviteSMS };

/** The sentence the console shows about the invitation SMS. */
export function inviteSMSNote(l: InvitedLogin): string {
  switch (l.sms) {
    case "sent":
      return `We texted ${l.mobile} with the sign-in link.`;
    case "failed":
      return `The SMS to ${l.mobile} did not go out -- tell them yourself.`;
    default:
      return "No SMS was sent -- tell them yourself.";
  }
}

export interface InviteInput {
  firstname: string;
  lastname: string;
  mobile: string;
}

const op = (ns: string) => `/operator/${encodeURIComponent(ns)}`;

export async function listOperatorLogins(identity: CallerIdentity, ns: string, kind: LoginKind, signal?: AbortSignal): Promise<OperatorLogin[]> {
  const r = await callGateway<OperatorLogin[] | null>(identity, config().gatewayBase, `${op(ns)}/operatorlogins?kind=${kind}`, signal ? { signal } : {});
  return r.data ?? [];
}

function cleanInvite(i: InviteInput): InviteInput {
  return { firstname: i.firstname.trim(), lastname: i.lastname.trim(), mobile: i.mobile.trim() };
}

export async function inviteStaff(identity: CallerIdentity, ns: string, input: InviteInput, roles: StaffRole[]): Promise<InvitedLogin> {
  const r = await callGateway<{ login: OperatorLogin; sms?: InviteSMS }>(identity, config().gatewayBase, `${op(ns)}/staff`, {
    method: "POST",
    body: { ...cleanInvite(input), roles: [...roles].sort() },
  });
  return { ...r.data.login, ...(r.data.sms ? { sms: r.data.sms } : {}) };
}

export async function inviteSubOperatorOwner(identity: CallerIdentity, parent: string, label: string, input: InviteInput): Promise<InvitedLogin> {
  const r = await callGateway<{ login: OperatorLogin; sms?: InviteSMS }>(identity, config().gatewayBase,
    `${op(parent)}/suboperators/${encodeURIComponent(label)}/owner`, { method: "POST", body: cleanInvite(input) });
  return { ...r.data.login, ...(r.data.sms ? { sms: r.data.sms } : {}) };
}

export type LoginAction = "cancel" | "disable" | "enable";

export async function setOperatorLoginStatus(identity: CallerIdentity, ns: string, id: number, action: LoginAction, note = ""): Promise<OperatorLogin> {
  const r = await callGateway<OperatorLogin>(identity, config().gatewayBase, `${op(ns)}/operatorlogins/${id}/${action}`, {
    method: "PUT",
    ...(note.trim() ? { body: { note: note.trim() } } : {}),
  });
  return r.data;
}

export async function setOperatorLoginRoles(identity: CallerIdentity, ns: string, id: number, roles: StaffRole[]): Promise<OperatorLogin> {
  const r = await callGateway<OperatorLogin>(identity, config().gatewayBase, `${op(ns)}/operatorlogins/${id}/roles`, {
    method: "PUT",
    body: { roles: [...roles].sort() },
  });
  return r.data;
}

/**
 * Hand an accepted login to a person again (radaccess v1.147, owner
 * 2026-10-24): a new mobile replaces the person, the same mobile is a reset.
 * The login is switched off at once and goes back to "Invited"; the person
 * accepts it like an invitation -- proves the mobile, sets the password -- and
 * keeps the same login and roles. The operator never sets the password.
 */
export async function reissueOperatorLogin(identity: CallerIdentity, ns: string, id: number, input: InviteInput, note = ""): Promise<InvitedLogin> {
  // radaccess v1.147 answered the row itself; v1.150+ answers {login, sms}.
  const r = await callGateway<OperatorLogin | { login: OperatorLogin; sms?: InviteSMS }>(identity, config().gatewayBase, `${op(ns)}/operatorlogins/${id}/reissue`, {
    method: "PUT",
    body: { ...cleanInvite(input), ...(note.trim() ? { note: note.trim() } : {}) },
  });
  const d = r.data;
  return "login" in d ? { ...d.login, ...(d.sms ? { sms: d.sms } : {}) } : d;
}

/** Whether a row can be handed to a person again: accepted logins only. */
export function canReissue(l: Pick<OperatorLogin, "status">): boolean {
  return l.status === "ACTIVE" || l.status === "DISABLED";
}

/** Superadmin: the approved applicant's sign-up login becomes the operator's own. */
export async function provisionApplication(identity: CallerIdentity, applicationId: string, namespace: string): Promise<{ namespace: string; status: string; note: string }> {
  const r = await callGateway<{ namespace: string; status: string; note: string }>(identity, config().gatewayBase,
    `${op(identity.namespace || "net")}/application/${encodeURIComponent(applicationId)}/provision`,
    { method: "POST", ...(namespace.trim() ? { body: { namespace: namespace.trim().toLowerCase() } } : {}) });
  return r.data;
}

/** Which actions a row offers, by status. Mirrors radaccess loginTransitions. */
export function actionsFor(l: Pick<OperatorLogin, "status">): LoginAction[] {
  switch (l.status) {
    case "PENDING":
      return ["cancel"];
    case "ACTIVE":
      return ["disable"];
    case "DISABLED":
      return ["enable"];
    default:
      return [];
  }
}

export function statusLabel(s: LoginStatus): string {
  switch (s) {
    case "PENDING":
      return "Invited";
    case "ACTIVE":
      return "Active";
    case "DISABLED":
      return "Disabled";
    case "CANCELLED":
      return "Cancelled";
    case "EXPIRED":
      return "Invitation expired";
  }
}

export function roleLabels(roles: readonly string[]): string {
  return roles.map((r) => [...STAFF_ROLES, ...PLATFORM_STAFF_ROLES].find((x) => x.role === r)?.label ?? (r === "operator" ? "Owner" : r)).join(", ");
}

// ---- Public: accepting an invitation (no identity, no token) ----

/** Who invited the person, and as what -- read from the invitation, never sent. */
export interface InvitationInfo {
  kind: LoginKind;
  namespace: string;
  invitedby: string;
  firstname: string;
  lastname: string;
  roles: string[];
}

export type VerifyInviteOutcome =
  | { kind: "verified"; acceptTicket: string; expiresIn: number; invitation: InvitationInfo }
  | { kind: "not_verified" }
  | { kind: "no_invitation"; message: string }
  | { kind: "conflict"; message: string }
  | { kind: "busy"; retryAfter: number }
  | { kind: "retry"; message: string }
  | { kind: "unavailable"; status: number | null };

export type UsernameCheck =
  | { kind: "available" }
  | { kind: "taken" }
  | { kind: "invalid"; problem: string }
  | { kind: "ticket_expired" }
  | { kind: "busy"; retryAfter: number }
  | { kind: "unavailable"; status: number | null };

export type AcceptOutcome =
  | { kind: "created"; namespace: string; roles: string[]; note: string; username: string }
  | { kind: "incomplete"; problems: string[] }
  /** Someone took the username first. The refused accept spent its ticket: retry on acceptTicket, the NEW one. */
  | { kind: "username_taken"; acceptTicket: string }
  /** The accept ticket ran out: prove the mobile again. */
  | { kind: "ticket_expired" }
  | { kind: "not_verified" }
  | { kind: "no_invitation"; message: string }
  | { kind: "conflict"; message: string }
  | { kind: "busy"; retryAfter: number }
  | { kind: "retry"; message: string }
  | { kind: "unavailable"; status: number | null };

const text = (v: unknown): string => (typeof v === "string" ? v : "");
const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
const inviteUrl = (path: string) => `${config().gatewayBase}/operator/invite/${path}`;
const acceptUrl = () => inviteUrl("accept");

/** The rule shown under the username field. Mirrors radaccess's check. */
export const USERNAME_RULE = "4–30 characters: starts with a letter, then lower-case letters, digits, . _ or -. No @.";

/**
 * Why a username is refused, or null. The caller lower-cases as the person
 * types, so an upper-case letter here is a problem, not something to fix.
 * 🔑 Never an `@`: a username must not read as an email, or sign-in could
 * not tell which one was typed.
 */
export function usernameProblem(s: string): string | null {
  if (s.includes("@")) return "A username cannot contain @.";
  if (/\s/.test(s)) return "A username cannot contain spaces.";
  if (s.length < 4) return "A username needs at least 4 characters.";
  if (s.length > 30) return "A username can have at most 30 characters.";
  if (!/^[a-z]/.test(s)) return "A username must start with a letter.";
  if (!/^[a-z][a-z0-9._-]*$/.test(s)) return "Use only lower-case letters, digits, . _ and -.";
  return null;
}

async function postPublic(url: string, payload: unknown, signal?: AbortSignal) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(withHuman(url, payload)),
    ...(signal ? { signal } : {}),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  humanRefused(url, res.status, body);
  return { res, body };
}

/** Seconds to wait after a 429: the Retry-After header, else the body, else a minute. */
function retryAfterOf(res: Response, body: Record<string, unknown>): number {
  const h = Number(res.headers.get("Retry-After"));
  if (Number.isFinite(h) && h > 0) return h;
  return typeof body.retryAfter === "number" && body.retryAfter > 0 ? body.retryAfter : 60;
}

/**
 * Whether accepting can work at all, asked BEFORE an SMS code is sent. An
 * unconfigured route answers 503 before reading the body; a configured one
 * refuses {} with 422 before it calls MSG91 or looks at any invitation
 * (radaccess handler/operatorlogin.go AcceptInvitation). So the probe says
 * nothing about any invitation or mobile.
 */
export async function acceptAvailability(signal?: AbortSignal): Promise<"available" | "unavailable" | "unknown"> {
  let res: Response;
  try {
    res = await fetch(acceptUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: "{}",
      ...(signal ? { signal } : {}),
    });
  } catch {
    return "unknown";
  }
  if (res.status === 503) return "unavailable";
  if (res.status === 422) return "available";
  // With the human check on, {} is refused for the missing token first: the
  // route is there and keyed, so accepting can work.
  if (res.status === 400 && (await res.json().catch(() => ({})) as { error?: string }).error === "captcha_required") return "available";
  return "unknown";
}

/**
 * Step 1: the widget proved the mobile; radaccess confirms the token with
 * MSG91 and answers the pending invitation with an accept ticket (15 min).
 * The mobile goes as 10 digits, as the contract names it.
 */
export type TestCodeOutcome =
  | { kind: "sent"; requestId: string; resendIn: number }
  | { kind: "refused"; message: string }
  | { kind: "busy"; retryAfter: number }
  | { kind: "unavailable" };

/**
 * A code for a TEST mobile (radaccess handler/testcode.go, owner 2026-10-24):
 * written to the inviting operator's SMS log, never texted. Only for a test
 * number whose invitation sits in a test operator. The code is never in the
 * answer; the page sends `requestId.code` as the widget token.
 */
export async function sendInviteTestCode(mobile: string, signal?: AbortSignal): Promise<TestCodeOutcome> {
  const e164 = normalizeIndianMobile(mobile);
  if (e164 === null) return { kind: "refused", message: "Enter the 10-digit mobile number the operator invited." };
  let r;
  try {
    r = await postPublic(inviteUrl("testcode"), { mobile: e164.slice(3) }, signal);
  } catch {
    return { kind: "unavailable" };
  }
  const { res, body } = r;
  if (res.status === 200 && text(body.requestId)) {
    return { kind: "sent", requestId: text(body.requestId), resendIn: typeof body.resendIn === "number" ? body.resendIn : 30 };
  }
  if (res.status === 429) return { kind: "busy", retryAfter: retryAfterOf(res, body) };
  if (res.status === 503 || res.status >= 500) return { kind: "unavailable" };
  return { kind: "refused", message: text(body.error) || "No code could be made for this number." };
}

/** The widget token for a test code. */
export const testCodeToken = (requestId: string, code: string) => `${requestId}.${code.trim()}`;

export async function verifyInvitation(input: { mobile: string; widgetToken: string }, signal?: AbortSignal): Promise<VerifyInviteOutcome> {
  const e164 = normalizeIndianMobile(input.mobile);
  const widgettoken = input.widgetToken.trim();
  if (e164 === null || !widgettoken) return { kind: "not_verified" };
  let r;
  try {
    r = await postPublic(inviteUrl("verify"), { mobile: e164.slice(3), widgettoken }, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  const { res, body } = r;
  switch (res.status) {
    case 200: {
      const acceptTicket = text(body.acceptTicket);
      const inv = (typeof body.invitation === "object" && body.invitation !== null ? body.invitation : {}) as Record<string, unknown>;
      if (!acceptTicket) return { kind: "unavailable", status: 200 };
      return {
        kind: "verified",
        acceptTicket,
        expiresIn: typeof body.expiresIn === "number" ? body.expiresIn : 900,
        invitation: {
          kind: inv.kind === "suboperator" ? "suboperator" : "staff",
          namespace: text(inv.namespace),
          invitedby: text(inv.invitedby),
          firstname: text(inv.firstname),
          lastname: text(inv.lastname),
          roles: strings(inv.roles),
        },
      };
    }
    case 401:
      return { kind: "not_verified" };
    case 404:
      return { kind: "no_invitation", message: text(body.error) || "There is no pending invitation for this mobile number." };
    case 409:
      return { kind: "conflict", message: text(body.error) || "This mobile number cannot accept the invitation." };
    case 429:
      return { kind: "busy", retryAfter: retryAfterOf(res, body) };
    case 502:
      return { kind: "retry", message: text(body.error) || "Something failed part-way. Try again in a minute." };
    default:
      return { kind: "unavailable", status: res.status };
  }
}

/** Is this username free? Refused here, without a request, when the rule fails. */
export async function checkUsername(input: { acceptTicket: string; username: string }, signal?: AbortSignal): Promise<UsernameCheck> {
  const problem = usernameProblem(input.username);
  if (problem !== null) return { kind: "invalid", problem };
  let r;
  try {
    r = await postPublic(inviteUrl("username"), { acceptTicket: input.acceptTicket, username: input.username }, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  const { res, body } = r;
  if (res.status === 200 && body.available === true) return { kind: "available" };
  if (res.status === 200 && body.available === false) {
    if (body.reason === "taken") return { kind: "taken" };
    return { kind: "invalid", problem: text(body.problem) || "This username cannot be used." };
  }
  if (res.status === 410) return { kind: "ticket_expired" };
  if (res.status === 429) return { kind: "busy", retryAfter: retryAfterOf(res, body) };
  return { kind: "unavailable", status: res.status };
}

/** The accept body: on an accept ticket (this release), or the old mobile + widget token one. */
export type AcceptInput =
  | { acceptTicket: string; username: string; password: string }
  | { mobile: string; password: string; widgetToken: string };

export async function acceptInvitation(input: AcceptInput, signal?: AbortSignal): Promise<AcceptOutcome> {
  let payload: Record<string, string>;
  if ("acceptTicket" in input) {
    const problem = usernameProblem(input.username);
    if (problem !== null) return { kind: "incomplete", problems: [problem] };
    if (!input.acceptTicket) return { kind: "ticket_expired" };
    payload = { acceptTicket: input.acceptTicket, password: input.password, username: input.username };
  } else {
    const mobile = normalizeIndianMobile(input.mobile);
    if (mobile === null) return { kind: "incomplete", problems: ["Enter a 10-digit Indian mobile number."] };
    const widgettoken = input.widgetToken.trim();
    if (!widgettoken) return { kind: "not_verified" };
    payload = { mobile, widgettoken, password: input.password };
  }
  let r;
  try {
    r = await postPublic(acceptUrl(), payload, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  const { res, body } = r;
  switch (res.status) {
    case 201:
      return {
        kind: "created",
        namespace: text(body.namespace),
        roles: strings(body.roles),
        note: text(body.note),
        username: text(body.username),
      };
    case 400:
    case 422: {
      const listed = strings(body.problems);
      return { kind: "incomplete", problems: listed.length > 0 ? listed : [text(body.error) || "The details are incomplete."] };
    }
    case 401:
      return { kind: "not_verified" };
    case 404:
      return { kind: "no_invitation", message: text(body.error) || "There is no pending invitation for this mobile number." };
    case 409:
      if (body.error === "username_taken") return { kind: "username_taken", acceptTicket: text(body.acceptTicket) };
      return { kind: "conflict", message: text(body.error) || "This mobile number cannot accept the invitation." };
    case 410:
      return { kind: "ticket_expired" };
    case 429:
      return { kind: "busy", retryAfter: retryAfterOf(res, body) };
    case 502:
      return { kind: "retry", message: text(body.error) || "Something failed part-way. Try again in a minute." };
    default:
      return { kind: "unavailable", status: res.status };
  }
}
